import React, { useId, useMemo, useState } from "react";
import Select, { components } from "react-select";
import moment from "moment-timezone";
import { getTimeZones } from "@vvo/tzdb";
import { FiCheck, FiGlobe, FiSearch } from "react-icons/fi";
import { timeZoneLabel, timeZoneOffset } from "./timeZones";
import "./TimeZoneSelect.css";

const ZoneControl = ({ children, ...props }) => <components.Control {...props}>
  {props.menuIsOpen ? <FiSearch className="schedule-zone-icon" aria-hidden="true" /> : <FiGlobe className="schedule-zone-icon" aria-hidden="true" />}
  {children}
</components.Control>;

const ZoneOption = ({ children, ...props }) => <components.Option {...props}>
  {children}<span className="schedule-zone-check">{props.isSelected && <FiCheck aria-hidden="true" />}</span>
</components.Option>;

const selectComponents = { Control: ZoneControl, Option: ZoneOption };

export default function TimeZoneSelect({ value, onChange, localZone, scheduleZone, practitionerZone, organizationZone, disabled = false, date }) {
  const id = useId();
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const options = useMemo(() => {
    const shortcuts = [{ key: "local", value: "local", zone: localZone, label: "My time zone", detail: timeZoneLabel(localZone) }];
    if (practitionerZone) shortcuts.push({ key: "practitioner", value: practitionerZone, zone: practitionerZone, label: "Practitioner", detail: timeZoneLabel(practitionerZone) });
    if (scheduleZone) shortcuts.push({ key: "schedule", value: scheduleZone, zone: scheduleZone, label: "Schedule", detail: timeZoneLabel(scheduleZone) });
    if (organizationZone) shortcuts.push({ key: "organization", value: organizationZone, zone: organizationZone, label: "Organization", detail: timeZoneLabel(organizationZone) });
    const shortcutZones = new Set(shortcuts.map((option) => option.value));
    return [
      { label: "Quick choices", options: shortcuts },
      { label: "All time zones", options: [...getTimeZones(), { name: "UTC", countryName: "Coordinated Universal Time" }]
        .filter((zone) => !shortcutZones.has(zone.name) && moment.tz.zone(zone.name))
        .map((zone) => ({ key: zone.name, value: zone.name, zone: zone.name,
          label: timeZoneLabel(zone.name), detail: timeZoneLabel(zone.name) === zone.countryName ? "" : zone.countryName,
          search: [zone.name, ...(zone.group || []), ...(zone.mainCities || []), zone.alternativeName].join(" ") })) },
    ];
  }, [localZone, scheduleZone, practitionerZone, organizationZone]);
  const allOptions = options.flatMap((group) => group.options);
  const selected = allOptions.find((option) => option.key === selectedKey && option.value === value) ||
    allOptions.find((option) => option.value === value) ||
    (moment.tz.zone(value) ? { key: value, value, zone: value, label: timeZoneLabel(value) } : null);
  return <div className="schedule-zone-select">
    <label htmlFor={id}>Times shown in</label>
    <Select inputId={id} className="schedule-zone-picker" classNamePrefix="schedule-zone" options={options} value={selected}
      components={selectComponents} getOptionValue={(option) => option.key}
      getOptionLabel={(option) => `${option.label} ${option.detail || ""}`}
      controlShouldRenderValue={!menuOpen} onMenuOpen={() => setMenuOpen(true)} onMenuClose={() => setMenuOpen(false)}
      isSearchable isClearable={false} isDisabled={disabled} menuPlacement="auto"
      menuPortalTarget={document.body} menuPosition="fixed"
      styles={{ menuPortal: (base) => ({ ...base, zIndex: 1080 }), menu: (base) => ({ ...base, width: "max(100%, min(360px, calc(100vw - 32px)))", right: 0 }) }}
      placeholder="Search city or time zone" noOptionsMessage={() => "No matching time zones"}
      filterOption={({ label, value: optionValue, data }, input) => {
        const normalize = (text) => text.toLowerCase().replace(/[^a-z0-9]/g, "");
        return normalize(`${label} ${optionValue} ${data.zone} ${data.detail || ""} ${data.search || ""}`).includes(normalize(input));
      }}
      onChange={(option) => { setSelectedKey(option.key); onChange(option.value); }}
      formatOptionLabel={(option, { context }) => context === "value" ?
        <span className="schedule-zone-value"><span>{timeZoneLabel(option.zone)}</span><small>{timeZoneOffset(option.zone, date)}</small></span> :
        <div className="schedule-zone-option"><span className="schedule-zone-option-copy"><span>{option.label}</span>{option.detail && <small>{option.detail}</small>}</span>
          <span className="schedule-zone-offset">{timeZoneOffset(option.zone, date)}</span></div>} />
  </div>;
}
